/**
 * Phase 106 (Plans 106-08, 106-12) — TanStack Query hooks for the fleet
 * console.
 *
 * `useFleetInstances`: refetchInterval: 20_000 — an ECS rolling deployment
 * takes roughly two to three minutes (D-03), so a 20-second poll surfaces a
 * rollout transition within one screenful of waiting without hammering two
 * AWS API calls (DescribeServices + DescribeTaskDefinition) per instance per
 * tick. refetchIntervalInBackground: false stops polling on window blur,
 * matching useCronJobs.ts's precedent.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthOptional } from '../services/authContext';
import {
  getFleetInstances,
  previewInstanceModules,
  saveInstanceModules,
  listSuperadmins,
  grantSuperadmin,
  revokeSuperadmin,
} from '../services/api/fleet';
import type {
  FleetListing,
  ModuleChangePreview,
  ModuleId,
  SaveModulesResult,
  SuperadminHolder,
  SuperadminMutationResult,
} from '../services/api/fleet';

export function useFleetInstances() {
  return useQuery<FleetListing>({
    queryKey: ['fleet-instances'],
    queryFn: () => getFleetInstances(),
    refetchInterval: 20_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
}

/**
 * A preview is deliberately a `useMutation`, not a `useQuery` — it is a
 * POST carrying a request body (the candidate module set) that must not be
 * cached across checkbox states; every checkbox change should fire a fresh
 * server call rather than resolve to a stale cached result keyed on
 * whatever the previous checkbox state happened to be. Never invalidates
 * anything on success — a preview is read-only (Plan 106-09).
 */
export function usePreviewInstanceModules(instanceId: string) {
  return useMutation<
    ModuleChangePreview,
    Error,
    { modules: ModuleId[]; moduleEnv?: Record<string, string> }
  >({
    mutationFn: ({ modules, moduleEnv }) => previewInstanceModules(instanceId, modules, moduleEnv),
  });
}

/**
 * On success, invalidates `['fleet-instances']` on EVERY outcome —
 * including `refused` — because a refusal can itself reveal that the
 * manifest moved underneath the operator (e.g. a stale `baseSha` surfaced
 * as a 409 from a sibling save elsewhere), and because `committed` /
 * `committed_and_triggered` both change the dashboard's desired state and
 * should make it start polling the real rollout immediately.
 */
export function useSaveInstanceModules(instanceId: string) {
  const queryClient = useQueryClient();
  return useMutation<
    SaveModulesResult,
    Error,
    { modules: ModuleId[]; baseSha: string; moduleEnv?: Record<string, string> }
  >({
    mutationFn: ({ modules, baseSha, moduleEnv }) =>
      saveInstanceModules(instanceId, modules, baseSha, moduleEnv),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fleet-instances'] });
    },
  });
}

/** No poll interval — the superadmin population changes only through this UI. */
export function useSuperadmins() {
  return useQuery<SuperadminHolder[]>({
    queryKey: ['fleet-superadmins'],
    queryFn: () => listSuperadmins(),
  });
}

export function useGrantSuperadmin() {
  const queryClient = useQueryClient();
  return useMutation<SuperadminMutationResult, Error, number>({
    mutationFn: (userId) => grantSuperadmin(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fleet-superadmins'] });
    },
  });
}

/**
 * Also invalidates `['fleet-instances']` when the revoked target is the
 * acting user themselves — a self-revoke ends the actor's own access on
 * their very next request (P-44; `requireSuperadmin` re-reads the column
 * fresh from the DB every time), so the console should stop assuming it can
 * still write to fleet endpoints.
 */
export function useRevokeSuperadmin() {
  const queryClient = useQueryClient();
  const auth = useAuthOptional();
  return useMutation<SuperadminMutationResult, Error, number>({
    mutationFn: (userId) => revokeSuperadmin(userId),
    onSuccess: (_result, userId) => {
      queryClient.invalidateQueries({ queryKey: ['fleet-superadmins'] });
      if (auth?.user?.id === userId) {
        queryClient.invalidateQueries({ queryKey: ['fleet-instances'] });
      }
    },
  });
}
